import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUserPolicies,
  selectUserPoliciesLoader,
  selectUserPoliciesData,
} from "../../redux/features/myPolicySlice";
import { selectLoginTokenInfo } from "../../redux/features/loginRegisterSlice";
import PolicyCard from "../../components/policyCard/policyCard";
import SkeletonLoader from "../../components/common/Loader/SkeletonLoader";
import { getClientSource } from "../../utils/getClientSource";
import useLanguageTranslate from "../../hooks/useLanguageTranslate";
import { POLICY_TYPES } from "../../components/policyCard/data";

const MyPolicies = () => {
  const tokenInfo = useSelector(selectLoginTokenInfo);
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();

  useEffect(() => {
    let payload = {
      Cpr: tokenInfo?.userName,
      Channel: getClientSource(),
    };
    if (tokenInfo) dispatch(fetchUserPolicies(payload));
  }, []);

  const userPolicies = useSelector(selectUserPoliciesData);
  const IsLoading = useSelector(selectUserPoliciesLoader);

  // Transform policy data based on type to match cancel policy card display
  const transformPolicyData = (policy) => {

    console.log("policy", policy);
    const productCode = policy?.ProductCode;
    const transformedPolicy = { ...policy };

    switch (productCode) {
      case POLICY_TYPES.homeInsurance:
        // Home Insurance: Show "Block No X, City" as label
        // If Block and City exist in the data, use them
        if (policy?.Block || policy?.City) {
          const blockNo = policy?.Block || "N/A";
          const city = policy?.City || "N/A";
          transformedPolicy.Model = `Block No ${blockNo}, ${city}`;
        } else if (policy?.Model && policy.Model.includes("Block")) {
          // Already has the format we need
          transformedPolicy.Model = policy.Model;
        } else if (policy?.Address) {
          // Try to extract from address
          transformedPolicy.Model = policy.Address;
        }
        break;

      case POLICY_TYPES.domesticHelper:
        // Domestic Helper: Show helper name as label
        // Priority: Name > HelperName > Model
        if (policy?.Name) {
          transformedPolicy.Model = policy.Name;
        } else if (policy?.HelperName) {
          transformedPolicy.Model = policy.HelperName;
          transformedPolicy.Name = policy.HelperName;
        } else if (!policy?.Model || policy.Model === "Domestic Helper Policy") {
          // If no name fields, keep whatever Model is there
          transformedPolicy.Model = policy?.Model || "";
        }
        break;

      case POLICY_TYPES.smartDevice:
      case "0213":
        // Smart Device: Show device type as label
        // Priority: DeviceTypeName > DeviceType > DeviceName > Model
        if (policy?.DeviceTypeName) {
          transformedPolicy.Model = policy.DeviceTypeName;
        } else if (policy?.DeviceType) {
          transformedPolicy.Model = policy.DeviceType;
          transformedPolicy.DeviceTypeName = policy.DeviceType;
        } else if (policy?.DeviceName) {
          transformedPolicy.Model = policy.DeviceName;
          transformedPolicy.DeviceTypeName = policy.DeviceName;
        } else if (!policy?.Model || policy.Model === "Smart Device") {
          transformedPolicy.Model = policy?.Model || "";
        }
        break;

      case POLICY_TYPES.personalAccident:
      case "0202":
      case "0208":
        // Personal Accident: Show CPR as label
        // Priority: CPR > InsuredCPR > Model
        if (policy?.CPR) {
          transformedPolicy.Model = policy.CPR;
        } else if (policy?.InsuredCPR) {
          transformedPolicy.Model = policy.InsuredCPR;
          transformedPolicy.CPR = policy.InsuredCPR;
        }
        break;

      case POLICY_TYPES.personalCyber:
      case "0640": // Personal Cyber actual product code from API
        // Personal Cyber: Show CPR as label
        // Priority: CPR > InsuredCPR > Model
        if (policy?.CPR) {
          transformedPolicy.Model = policy.CPR;
        } else if (policy?.InsuredCPR) {
          transformedPolicy.Model = policy.InsuredCPR;
          transformedPolicy.CPR = policy.InsuredCPR;
        }
        break;

      case POLICY_TYPES.tranvelInsurance:
        // Travel is already working correctly with days calculation
        break;

      default:
        // Motor and other types - keep as is
        break;
    }

    return transformedPolicy;
  };

  return (
    <>
      {IsLoading && !userPolicies?.length && <SkeletonLoader />}
      {userPolicies
        ?.filter((each) => each.PolicyStatus != "EXPIRED")
        ?.slice()
        ?.sort((a, b) => {
          const aTime = new Date(a?.EndDate || a?.to_date || 0).getTime();
          const bTime = new Date(b?.EndDate || b?.to_date || 0).getTime();
          if (isNaN(aTime) && isNaN(bTime)) return 0;
          if (isNaN(aTime)) return 1;
          if (isNaN(bTime)) return -1;
          return aTime - bTime;
        })
        ?.map((each, index) => {
          const transformedPolicy = transformPolicyData(each);
          return (
            <PolicyCard
              key={transformedPolicy?.policy_number || index}
              policy_name={transformedPolicy.policy_name}
              policy_label={transformedPolicy.policy_label}
              policy_number={transformedPolicy.policy_number}
              from_date={transformedPolicy.from_date}
              to_date={transformedPolicy.to_date}
              policy_bg_image={transformedPolicy.policy_bg_image}
              expire_days={transformedPolicy.expire_days}
              is_text_dark={transformedPolicy.is_text_dark}
              mapping_key={transformedPolicy.id}
              data={transformedPolicy}
            />
          );
        })}
    </>
  );
};

export default MyPolicies;
